/* ---------------------------------------------------------
 * src/API/creatorTokenUtil.js - ENHANCED VERSION
 * ---------------------------------------------------------
 * Improved cookie handling and storage reliability
 * ------------------------------------------------------- */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import Cookies from 'js-cookie';
import axios from 'axios';
import { ConfigValue } from '../Config';
import { clearMemory as clearUserMem } from './token-util';

/* ─────────────────────────── Keys ─────────────────────────── */
const KEY = ConfigValue.AUTH_CREATOR_TOKEN_KEY; // "blitz-creator-token"
const INFO_KEY = ConfigValue.AUTH_CREATOR_USER_INFO;
const REFRESH_KEY = `${KEY}-refresh`;
const BASE_API = process.env.REACT_APP_BASE_URL || 'https://blitz-app-u3rzf.ondigitalocean.app/api';

const USER_KEY = ConfigValue.AUTH_TOKEN_KEY;
const USER_INFO_KEY = ConfigValue.AUTH_USER_INFO;

const PERSIST_DAYS = 365 * 100;
const MAX_COOKIE_SIZE = 3500; // Safe cookie size limit
const REFRESH_BUFFER_MS = 5 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 15 * 1000;
const DEFAULT_TOKEN_LIFETIME_MS = 60 * 60 * 1000;

/* ─────────────────── In-memory caches ─────────────────────── */
let tokenCache = null;
let infoCache = null;
let refreshTokenCache = null;
let refreshTimer = null;
let refreshInFlight = null;

// Enhanced logging with performance tracking
function logCacheState(operation) {
  const start = performance.now();
  console.log(`🔍 [CreatorStorage] ${operation}:`, {
    tokenCache: tokenCache ? `${tokenCache.length} chars` : 'NULL',
    infoCache: infoCache ? `${JSON.stringify(infoCache).length} chars` : 'NULL',
    platform: Capacitor.getPlatform(),
    timestamp: new Date().toISOString(),
    performance: `${(performance.now() - start).toFixed(2)}ms`
  });
}

function parseJwtExpMs(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;

    const payloadBase64 = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padded = payloadBase64 + '='.repeat((4 - (payloadBase64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    if (typeof payload.exp !== 'number') return null;

    return payload.exp * 1000;
  } catch {
    return null;
  }
}

function clearRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleProactiveRefresh(expiresInSec = null) {
  clearRefreshTimer();
  if (!tokenCache) return;

  let expMs = null;
  if (typeof expiresInSec === 'number' && expiresInSec > 0) {
    expMs = Date.now() + expiresInSec * 1000;
  } else {
    expMs = parseJwtExpMs(tokenCache);
  }

  if (!expMs) {
    expMs = Date.now() + DEFAULT_TOKEN_LIFETIME_MS;
  }

  const delay = Math.max(MIN_REFRESH_DELAY_MS, expMs - Date.now() - REFRESH_BUFFER_MS);
  refreshTimer = setTimeout(() => {
    void refreshAccessTokenNow();
  }, delay);
}

/* ─────────────────────── Enhanced Initialization ───────────────────── */
export async function initCreatorAuthStorage() {
  console.log('🔍 [CreatorStorage] initCreatorAuthStorage called');
  logCacheState('BEFORE_INIT');
  
  try {
    if (Capacitor.isNativePlatform()) {
      console.log('🔍 [CreatorStorage] Using native platform storage');
      const [{ value: tok }, { value: inf }, { value: refresh }] = await Promise.all([
        Preferences.get({ key: KEY }),
        Preferences.get({ key: INFO_KEY }),
        Preferences.get({ key: REFRESH_KEY }),
      ]);
      
      tokenCache = tok || null;
      infoCache = inf ? JSON.parse(inf) : null;
      refreshTokenCache = refresh || null;
    } else {
      console.log('🔍 [CreatorStorage] Using web storage (cookies)');
      
      // Enhanced cookie reading with validation
      tokenCache = readCookieSafely(KEY);
      infoCache = readCookieSafely(INFO_KEY, true); // Parse JSON
      refreshTokenCache = readCookieSafely(REFRESH_KEY);
      
      // Validate data integrity
      if (tokenCache && !isValidJWT(tokenCache)) {
        console.warn('⚠️ [CreatorStorage] Invalid JWT found, clearing');
        tokenCache = null;
        refreshTokenCache = null;
        Cookies.remove(KEY, { path: '/' });
        Cookies.remove(REFRESH_KEY, { path: '/' });
      }
      
      if (infoCache && !isValidUserInfo(infoCache)) {
        console.warn('⚠️ [CreatorStorage] Invalid user info found, clearing');
        infoCache = null;
        Cookies.remove(INFO_KEY, { path: '/' });
      }
    }
    
    logCacheState('AFTER_INIT');

    if (tokenCache) {
      const expMs = parseJwtExpMs(tokenCache);
      if (expMs && expMs <= Date.now()) {
        await removeAuthToken();
        return { status: 'expired', hasToken: false, hasUserInfo: false };
      }
      scheduleProactiveRefresh();
    }
    
    // Post-initialization validation
    if (tokenCache && (!infoCache || !infoCache.creator_user)) {
      console.warn('⚠️ [CreatorStorage] Token exists but user info incomplete');
      return { status: 'incomplete', hasToken: true, hasUserInfo: false };
    }
    
    return { 
      status: tokenCache ? 'complete' : 'empty', 
      hasToken: !!tokenCache, 
      hasUserInfo: !!(infoCache?.creator_user) 
    };
    
  } catch (error) {
    console.error('❌ [CreatorStorage] Initialization error:', error);
    // Clear potentially corrupted data
    tokenCache = null;
    infoCache = null;
    return { status: 'error', error: error.message };
  }
}

/* ─────────────────── Safe Cookie Operations ───────────────────── */
function readCookieSafely(key, parseJSON = false) {
  try {
    const value = Cookies.get(key);
    if (!value) return null;
    
    if (parseJSON) {
      try {
        return JSON.parse(value);
      } catch (e) {
        console.warn(`⚠️ [CreatorStorage] Failed to parse JSON for ${key}:`, e);
        Cookies.remove(key, { path: '/' }); // Remove corrupted cookie
        return null;
      }
    }
    
    return value;
  } catch (error) {
    console.error(`❌ [CreatorStorage] Error reading cookie ${key}:`, error);
    return null;
  }
}

function setCookieSafely(key, value, isJSON = false) {
  try {
    const cookieValue = isJSON ? JSON.stringify(value) : value;
    
    // Check size before setting
    if (cookieValue.length > MAX_COOKIE_SIZE) {
      console.warn(`⚠️ [CreatorStorage] Cookie ${key} too large (${cookieValue.length} chars), using compression`);
      
      if (isJSON && key === INFO_KEY) {
        // Use compressed user info for large objects
        const compressed = compressUserInfo(value);
        const compressedString = JSON.stringify(compressed);
        
        if (compressedString.length > MAX_COOKIE_SIZE) {
          console.error('❌ [CreatorStorage] Even compressed data too large, using minimal fallback');
          const minimal = getMinimalUserInfo(value);
          Cookies.set(key, JSON.stringify(minimal), {
            expires: PERSIST_DAYS,
            secure: window.location.protocol === 'https:',
            sameSite: 'Lax',
            path: '/'
          });
          return true;
        }
        
        Cookies.set(key, compressedString, {
          expires: PERSIST_DAYS,
          secure: window.location.protocol === 'https:',
          sameSite: 'Lax',
          path: '/'
        });
        return true;
      }
      
      return false; // Can't save oversized non-JSON data
    }
    
    Cookies.set(key, cookieValue, {
      expires: PERSIST_DAYS,
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax',
      path: '/'
    });
    
    // Immediate verification
    const verification = Cookies.get(key);
    if (!verification) {
      console.error(`❌ [CreatorStorage] Failed to set cookie ${key}`);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error(`❌ [CreatorStorage] Error setting cookie ${key}:`, error);
    return false;
  }
}

/* ─────────────────── Data Validation ───────────────────── */
function isValidJWT(token) {
  if (typeof token !== 'string') return false;
  
  // Basic JWT structure check (3 parts separated by dots)
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  
  // Check if each part is valid base64
  try {
    parts.forEach(part => {
      if (part.length === 0) throw new Error('Empty part');
      // Basic base64 character check
      if (!/^[A-Za-z0-9_-]+$/.test(part)) throw new Error('Invalid characters');
    });
    return true;
  } catch (e) {
    return false;
  }
}

function isValidUserInfo(info) {
  return info && 
         typeof info === 'object' && 
         info.creator_user && 
         typeof info.creator_user.username === 'string';
}

/* ─────────────────── Data Compression Helpers ───────────────────── */
function compressUserInfo(userInfo) {
  if (!userInfo) return null;
  
  // Keep only essential fields to reduce cookie size
  return {
    creator_user: {
      username: userInfo.creator_user?.username,
      has_phone: userInfo.creator_user?.has_phone,
      creator: userInfo.creator_user?.creator ? {
        display_name: userInfo.creator_user.creator.display_name,
        pfphref: userInfo.creator_user.creator.pfphref,
        status: userInfo.creator_user.creator.status,
        // Remove large unnecessary fields
      } : null
    },
    // Keep any other essential top-level fields
    token: userInfo.token, // If stored in the info object
  };
}

function getMinimalUserInfo(userInfo) {
  return {
    creator_user: {
      username: userInfo?.creator_user?.username || 'unknown'
    }
  };
}

/* ───────────────────── Enhanced Token Operations ─────────────────────── */
export const getAuthToken = () => {
  console.log('🔍 [CreatorStorage] getAuthToken called:', tokenCache ? 'EXISTS' : 'NULL');
  return tokenCache;
};

export const getAuthInfo = () => {
  console.log('🔍 [CreatorStorage] getAuthInfo called:', infoCache ? 'EXISTS' : 'NULL');
  return infoCache;
};

export async function setAuthToken(tokenObj) {
  console.log('🔍 [CreatorStorage] setAuthToken called');
  
  const token = typeof tokenObj === 'string' ? tokenObj : tokenObj.token;
  const info = typeof tokenObj === 'string' ? null : tokenObj;
  const refreshToken = typeof tokenObj === 'string'
    ? refreshTokenCache
    : tokenObj.refresh_token ?? tokenObj.refreshToken ?? refreshTokenCache;
  
  // Validate input
  if (token && !isValidJWT(token)) {
    console.error('❌ [CreatorStorage] Invalid JWT provided');
    throw new Error('Invalid JWT token format');
  }
  
  if (info && !isValidUserInfo(info)) {
    console.warn('⚠️ [CreatorStorage] Invalid user info provided, will store minimal data');
  }
  
  tokenCache = token;
  infoCache = info;
  refreshTokenCache = refreshToken || null;
  
  console.log('🔍 [CreatorStorage] Data to store:', {
    tokenLength: token ? token.length : 0,
    infoSize: infoCache ? JSON.stringify(infoCache).length : 0,
    hasCreatorUser: infoCache?.creator_user ? 'YES' : 'NO'
  });

  if (Capacitor.isNativePlatform()) {
    await Promise.all([
      Preferences.set({ key: KEY, value: token }),
      Preferences.set({ key: INFO_KEY, value: JSON.stringify(infoCache || {}) }),
      refreshTokenCache
        ? Preferences.set({ key: REFRESH_KEY, value: refreshTokenCache })
        : Preferences.remove({ key: REFRESH_KEY }),
      Preferences.remove({ key: USER_KEY }),
      Preferences.remove({ key: USER_INFO_KEY }),
    ]);
    console.log('✅ [CreatorStorage] Saved to native storage');
  } else {
    console.log('🔍 [CreatorStorage] Setting cookies...');
    
    // Set token cookie
    const tokenSuccess = setCookieSafely(KEY, token, false);
    if (!tokenSuccess) {
      console.error('❌ [CreatorStorage] Failed to save token cookie');
      throw new Error('Failed to save authentication token');
    }
    
    // Set info cookie
    let infoSuccess = true;
    if (infoCache && typeof infoCache === 'object') {
      infoSuccess = setCookieSafely(INFO_KEY, infoCache, true);
      if (!infoSuccess) {
        console.warn('⚠️ [CreatorStorage] Failed to save full user info, trying minimal');
        // Try saving minimal info as fallback
        const minimal = getMinimalUserInfo(infoCache);
        infoSuccess = setCookieSafely(INFO_KEY, minimal, true);
      }
    } else {
      Cookies.remove(INFO_KEY, { path: '/' });
    }

    if (refreshTokenCache) {
      setCookieSafely(REFRESH_KEY, refreshTokenCache, false);
    } else {
      Cookies.remove(REFRESH_KEY, { path: '/' });
    }

    // Clear conflicting user cookies
    Cookies.remove(USER_KEY, { path: '/' });
    Cookies.remove(USER_INFO_KEY, { path: '/' });
    
    if (!infoSuccess) {
      console.warn('⚠️ [CreatorStorage] User info not fully saved - functionality may be limited');
    }
    
    // Enhanced verification
    setTimeout(() => {
      const verifyToken = Cookies.get(KEY);
      const verifyInfo = readCookieSafely(INFO_KEY, true);
      
      console.log('🔍 [CreatorStorage] Final verification:', {
        tokenSaved: !!verifyToken && verifyToken === token,
        infoSaved: !!verifyInfo,
        infoValid: isValidUserInfo(verifyInfo),
        username: verifyInfo?.creator_user?.username || 'N/A'
      });
      
      // Update cache with verified data
      if (verifyToken !== tokenCache || JSON.stringify(verifyInfo) !== JSON.stringify(infoCache)) {
        console.log('🔄 [CreatorStorage] Updating cache with verified data');
        tokenCache = verifyToken;
        infoCache = verifyInfo;
      }
    }, 100);
  }

  scheduleProactiveRefresh();
  clearUserMem();
}

export async function refreshAccessTokenNow() {
  if (refreshInFlight) return refreshInFlight;
  if (!tokenCache) return false;

  refreshInFlight = (async () => {
    let refreshed = false;
    try {
      const response = await axios.post(
        `${BASE_API}/auth/refresh`,
        {},
        {
          headers: { Authorization: `Bearer ${tokenCache}` },
          withCredentials: true,
          timeout: 120000,
        }
      );

      const data = response?.data || {};
      const nextToken = data.access_token || data.token;
      if (!nextToken) return false;

      const nextRefresh = data.refresh_token ?? refreshTokenCache;

      const nextInfo = infoCache
        ? {
            ...infoCache,
            token: nextToken,
            refresh_token: nextRefresh,
          }
        : infoCache;

      tokenCache = nextToken;
      refreshTokenCache = nextRefresh || null;
      infoCache = nextInfo;

      if (Capacitor.isNativePlatform()) {
        await Promise.all([
          Preferences.set({ key: KEY, value: nextToken }),
          refreshTokenCache
            ? Preferences.set({ key: REFRESH_KEY, value: refreshTokenCache })
            : Preferences.remove({ key: REFRESH_KEY }),
          nextInfo
            ? Preferences.set({ key: INFO_KEY, value: JSON.stringify(nextInfo) })
            : Promise.resolve(),
        ]);
      } else {
        setCookieSafely(KEY, nextToken, false);
        if (refreshTokenCache) {
          setCookieSafely(REFRESH_KEY, refreshTokenCache, false);
        } else {
          Cookies.remove(REFRESH_KEY, { path: '/' });
        }
        if (nextInfo) {
          setCookieSafely(INFO_KEY, nextInfo, true);
        }
      }

      scheduleProactiveRefresh(data.expires_in ?? null);
      refreshed = true;
      return true;
    } catch {
      return false;
    } finally {
      if (!refreshed && tokenCache) {
        const expMs = parseJwtExpMs(tokenCache);
        if (!expMs || expMs > Date.now()) {
          scheduleProactiveRefresh();
        }
      }
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Clear creator authentication credentials
 *
 * NOTE: device_token is intentionally NOT removed.
 * Device tokens persist across logout for trusted device functionality.
 */
export async function removeAuthToken() {
  console.log('🔍 [CreatorStorage] removeAuthToken called');
  logCacheState('BEFORE_REMOVE');

  clearRefreshTimer();
  refreshInFlight = null;

  tokenCache = null;
  infoCache = null;
  refreshTokenCache = null;

  if (Capacitor.isNativePlatform()) {
    await Promise.all([
      Preferences.remove({ key: KEY }),
      Preferences.remove({ key: INFO_KEY }),
      Preferences.remove({ key: REFRESH_KEY }),
      // blitz_device_token intentionally NOT removed
    ]);
    console.log('✅ [CreatorStorage] Removed from native storage');
  } else {
    Cookies.remove(KEY, { path: '/' });      // Remove: blitz-creator-token
    Cookies.remove(INFO_KEY, { path: '/' }); // Remove: creator info
    Cookies.remove(REFRESH_KEY, { path: '/' }); // Remove: creator refresh token
    // device_token cookie intentionally NOT removed

    // Enhanced removal verification
    setTimeout(() => {
      const checkToken = Cookies.get(KEY);
      const checkInfo = Cookies.get(INFO_KEY);
      const checkDeviceToken = Cookies.get('device_token');
      console.log('✅ [CreatorStorage] Removal verification:', {
        tokenRemoved: !checkToken,
        infoRemoved: !checkInfo,
        deviceTokenPreserved: !!checkDeviceToken  // Verify it's still there
      });
    }, 50);
  }

  logCacheState('AFTER_REMOVE');
}

export async function logoutAndClear() {
  const currentToken = tokenCache;

  try {
    if (currentToken) {
      await axios.post(
        `${BASE_API}/auth/logout`,
        {},
        {
          headers: { Authorization: `Bearer ${currentToken}` },
          withCredentials: true,
          timeout: 30000,
        }
      );
    }
  } catch {
    // best-effort logout; local cleanup still runs
  } finally {
    await removeAuthToken();
  }
}

/* ─────────────────── Enhanced Helper Functions ───────────────────── */
export const isAuthorized = () => {
  const result = Boolean(tokenCache);
  console.log('🔍 [CreatorStorage] isAuthorized:', result);
  return result;
};

export const hasCompleteAuth = () => {
  const complete = Boolean(tokenCache && infoCache?.creator_user?.username);
  console.log('🔍 [CreatorStorage] hasCompleteAuth:', complete);
  return complete;
};

export const hasBothRoles = () =>
  Boolean(Cookies.get(KEY) && Cookies.get(USER_KEY));

export const activeRole = () => {
  if (Cookies.get(KEY)) return 'creator';
  if (Cookies.get(USER_KEY)) return 'user';
  return null;
};

export function refreshCookieExpiry() {
  if (!Capacitor.isNativePlatform()) {
    if (tokenCache) {
      setCookieSafely(KEY, tokenCache, false);
    }
    if (infoCache) {
      setCookieSafely(INFO_KEY, infoCache, true);
    }
  }
}

export function clearMemory() {
  console.log('🔍 [CreatorStorage] clearMemory called');
  logCacheState('BEFORE_CLEAR_MEMORY');
  clearRefreshTimer();
  refreshInFlight = null;
  tokenCache = null;
  infoCache = null;
  refreshTokenCache = null;
  logCacheState('AFTER_CLEAR_MEMORY');
}

/* ─────────────────── Diagnostic Functions ───────────────────── */
export function getStorageDebugInfo() {
  const cookies = document.cookie;
  const creatorTokenCookie = Cookies.get(KEY);
  const creatorInfoCookie = Cookies.get(INFO_KEY);
  
  return {
    platform: Capacitor.getPlatform(),
    cookies: {
      all: cookies,
      creatorToken: creatorTokenCookie ? `${creatorTokenCookie.length} chars` : 'NULL',
      creatorInfo: creatorInfoCookie ? `${creatorInfoCookie.length} chars` : 'NULL',
    },
    cache: {
      token: tokenCache ? `${tokenCache.length} chars` : 'NULL',
      info: infoCache ? JSON.stringify(infoCache).length + ' chars' : 'NULL',
    },
    validation: {
      tokenValid: tokenCache ? isValidJWT(tokenCache) : false,
      infoValid: infoCache ? isValidUserInfo(infoCache) : false,
    },
    timestamp: new Date().toISOString()
  };
}

export function diagnoseAuthIssues() {
  const debug = getStorageDebugInfo();
  const issues = [];
  
  if (tokenCache && !isValidJWT(tokenCache)) {
    issues.push('Invalid JWT token format');
  }
  
  if (tokenCache && (!infoCache || !infoCache.creator_user)) {
    issues.push('Token exists but user info missing');
  }
  
  if (infoCache && !infoCache.creator_user?.username) {
    issues.push('User info exists but username missing');
  }
  
  if (!Capacitor.isNativePlatform()) {
    const tokenCookie = Cookies.get(KEY);
    const infoCookie = Cookies.get(INFO_KEY);
    
    if (tokenCache && !tokenCookie) {
      issues.push('Token in cache but not in cookie');
    }
    
    if (infoCache && !infoCookie) {
      issues.push('User info in cache but not in cookie');
    }
  }
  
  return {
    debug,
    issues,
    recommendations: issues.length > 0 ? [
      'Try refreshing the page',
      'If issues persist, log out and log back in',
      'Check browser cookie settings and storage limits'
    ] : ['Auth system appears healthy']
  };
}